/**
 * Google Gemini–inspired semantic colors (light + dark).
 * Use from theme `palette` where possible; import this only where the theme
 * object is unavailable (e.g. ECharts option builders).
 */

/** Light shell (Gemini web canvas) */
export const GEMINI_LIGHT_CANVAS = "#f0f4f9";
export const GEMINI_LIGHT_SURFACE = "#ffffff";
export const GEMINI_LIGHT_SURFACE_ALT = "#e8eef5";
export const GEMINI_LIGHT_BORDER = "#dadce0";
export const GEMINI_LIGHT_TEXT_PRIMARY = "#1f1f1f";
export const GEMINI_LIGHT_TEXT_SECONDARY = "#444746";
export const GEMINI_LIGHT_PRIMARY = "#1a73e8";
export const GEMINI_LIGHT_PRIMARY_DARK = "#1557b0";
export const GEMINI_LIGHT_ON_PRIMARY = "#ffffff";

/** Dark shell (Gemini-style) */
export const GEMINI_DARK_CANVAS = "#131314";
export const GEMINI_DARK_SURFACE = "#1f1f1f";
export const GEMINI_DARK_SURFACE_ELEVATED = "#282a2c";
export const GEMINI_DARK_BORDER = "#3c4043";
export const GEMINI_DARK_TEXT_PRIMARY = "#e3e3e3";
export const GEMINI_DARK_TEXT_SECONDARY = "#9aa0a6";
export const GEMINI_DARK_PRIMARY = "#8ab4f8";
export const GEMINI_DARK_PRIMARY_DIM = "#669df6";
export const GEMINI_DARK_ON_PRIMARY = "#062e6a";

/** Shared accents (Material / Google hues) */
export const GEMINI_RED = "#d93025";
export const GEMINI_RED_DARK = "#c5221f";
export const GEMINI_AMBER = "#f9ab00";
export const GEMINI_AMBER_DARK = "#f29900";
export const GEMINI_ORANGE = "#e8710a";
export const GEMINI_GREEN = "#1e8e3e";
export const GEMINI_GREEN_DARK = "#137333";
export const GEMINI_BLUE = "#1a73e8";
export const GEMINI_BLUE_DARK = "#1557b0";

/** Charts & gauges (ECharts) */
export const geminiChartColors = {
  light: {
    text: GEMINI_LIGHT_TEXT_PRIMARY,
    textMuted: GEMINI_LIGHT_TEXT_SECONDARY,
    axis: GEMINI_LIGHT_BORDER,
    splitLine: "#e8eef5",
    seriesBlue: GEMINI_BLUE,
    seriesBlueEnd: GEMINI_BLUE_DARK,
    seriesRed: GEMINI_RED,
    seriesRedEnd: GEMINI_RED_DARK,
    seriesGreen: GEMINI_GREEN,
    seriesGreenEnd: GEMINI_GREEN_DARK,
    gaugeLow: GEMINI_RED,
    gaugeMid: GEMINI_AMBER,
    gaugeHigh: GEMINI_GREEN,
  },
  dark: {
    text: GEMINI_DARK_TEXT_PRIMARY,
    textMuted: GEMINI_DARK_TEXT_SECONDARY,
    axis: GEMINI_DARK_BORDER,
    splitLine: "#3c4043",
    seriesBlue: GEMINI_DARK_PRIMARY,
    seriesBlueEnd: GEMINI_DARK_PRIMARY_DIM,
    seriesRed: "#f28b82",
    seriesRedEnd: GEMINI_RED,
    seriesGreen: "#81c995",
    seriesGreenEnd: GEMINI_GREEN,
    gaugeLow: "#f28b82",
    gaugeMid: GEMINI_AMBER,
    gaugeHigh: "#81c995",
  },
} as const;

export function geminiChartPalette(mode: "light" | "dark") {
  return geminiChartColors[mode];
}

/** Priority / default severity chips (tables, filters) */
export const geminiPriorityColors = {
  Critical: GEMINI_RED,
  High: GEMINI_ORANGE,
  Medium: GEMINI_AMBER,
  Low: GEMINI_GREEN,
  Informational: "#5f6368",
  /** @deprecated Legacy incident API values */
  CRISIS: GEMINI_RED,
  CRITICAL: GEMINI_RED,
  HIGH: GEMINI_ORANGE,
  MEDIUM: GEMINI_AMBER,
  LOW: GEMINI_GREEN,
  default: "#80868b",
} as const;

export function resolvePriorityColorKey(
  priority?: string,
): keyof typeof geminiPriorityColors {
  if (!priority) {
    return "default";
  }

  if (priority in geminiPriorityColors && priority !== "default") {
    return priority as keyof typeof geminiPriorityColors;
  }

  const legacyMap: Record<string, keyof typeof geminiPriorityColors> = {
    CRISIS: "Critical",
    CRITICAL: "Critical",
    HIGH: "High",
    MEDIUM: "Medium",
    LOW: "Low",
    INFORMATIONAL: "Informational",
  };

  return legacyMap[priority.toUpperCase()] ?? "default";
}

export function resolvePriorityColor(priority?: string): string {
  return geminiPriorityColors[resolvePriorityColorKey(priority)];
}

export function isMediumPriorityLabel(priority?: string): boolean {
  const key = resolvePriorityColorKey(priority);
  return key === "Medium" || key === "MEDIUM";
}

export const geminiStatusColors = {
  NEW: GEMINI_BLUE,
  ASSIGNED: GEMINI_AMBER,
  IN_PROGRESS: "#9334e6",
  ON_HOLD: "#5f6368",
  RESOLVED: GEMINI_GREEN,
  CLOSED: "#80868b",
  CANCELLED: GEMINI_RED,
  default: "#80868b",
} as const;
